import React, { useState, useEffect, useCallback } from 'react';
import { X, Archive, RotateCcw, Trash2, AlertCircle } from 'lucide-react';
import { api } from '../../api/client';

const ArchivedWorkshopsModal = ({ onClose }) => {
  const [workshops, setWorkshops] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadArchived = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await api.getArchivedWorkshops();
      setWorkshops(data);
    } catch (error) {
      console.error('Failed to load archived workshops:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadArchived(); }, [loadArchived]);

  const handleRestore = async (workshop) => {
    try {
      await api.restoreWorkshop(workshop.id);
      setWorkshops(workshops.filter(w => w.id !== workshop.id));
    } catch (error) {
      alert('Failed to restore: ' + error.message);
    }
  };

  const handlePurge = async (workshop) => {
    const confirmed = window.confirm(
      `Permanently delete "${workshop.name}" right now?\n\nThis skips the remaining ${workshop.days_remaining} day(s) in the archive and cannot be undone -- everything inside it will be gone for good.`
    );
    if (!confirmed) return;
    try {
      await api.purgeWorkshop(workshop.id);
      setWorkshops(workshops.filter(w => w.id !== workshop.id));
    } catch (error) {
      alert('Failed to purge: ' + error.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Archive className="w-5 h-5 text-[#01006A]" />
            Archived Workshops
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          <p className="text-sm text-gray-500 mb-4">
            Deleted workshops stay here for 30 days before being permanently removed. Restoring brings back the workshop exactly as it was -- nothing inside it was ever touched.
          </p>

          {isLoading ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : workshops.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <AlertCircle className="w-10 h-10 mx-auto mb-3 text-gray-400" />
              <p className="text-sm">No archived workshops.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {workshops.map(w => (
                <div key={w.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-1">
                    <h4 className="font-semibold text-gray-900">{w.name}</h4>
                    <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ml-2 ${
                      w.days_remaining <= 3 ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {w.days_remaining === 0 ? 'purging soon' : `${w.days_remaining} day${w.days_remaining !== 1 ? 's' : ''} left`}
                    </span>
                  </div>
                  {w.description && <p className="text-sm text-gray-600 mb-3">{w.description}</p>}
                  <div className="flex gap-2">
                    <button onClick={() => handleRestore(w)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-[#01006A] text-white rounded-lg hover:bg-[#010059] text-sm font-medium">
                      <RotateCcw className="w-3.5 h-3.5" />
                      Restore
                    </button>
                    <button onClick={() => handlePurge(w)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 text-sm font-medium">
                      <Trash2 className="w-3.5 h-3.5" />
                      Delete Forever
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArchivedWorkshopsModal;
